// Pure adapter that lifts the BFF detail-endpoint's flat row shape back
// into the nested `actor` / `target` shape the renderer expects.
//
// Lives in its own .ts module so AuditDetailPanel.tsx can stay
// component-only (react-refresh requirement).
//
// Pinned by AuditDetailPanel.test.ts — the row-click crash regression
// (2026-05-13: clicking a row threw `Cannot read properties of undefined
// (reading 'alias')` because the panel read evt.actor.alias but the
// detail endpoint returned actorAlias as a flat string).

import type { AuditEvent } from "./types";

export function adaptDetail(
  raw: Record<string, unknown> | null,
  fallback: AuditEvent,
): { evt: AuditEvent; rawDetails?: Record<string, unknown>; changesJson?: unknown } {
  if (raw === null) {
    return { evt: fallback };
  }

  const get = <T,>(key: string): T | undefined =>
    (raw as Record<string, T | undefined>)[key];

  const actor = {
    alias: (get<string>("actorAlias") ?? fallback.actor.alias) || "",
    displayName:
      get<string>("actorDisplayName") ?? fallback.actor.displayName ?? "",
    kind: get<string>("actorKind") ?? fallback.actor.kind ?? "user",
    role: get<string>("actorRole") ?? fallback.actor.role ?? "",
  };

  const targetId = get<string>("targetId");
  const targetKind = get<string>("targetKind");
  const target = targetId
    ? {
        kind: targetKind ?? "unknown",
        id: targetId,
        displayName: get<string>("target") ?? targetId,
      }
    : fallback.target;

  const evt: AuditEvent = {
    eventId: get<string>("eventId") ?? fallback.eventId,
    timestamp: get<string>("timestamp") ?? fallback.timestamp,
    eventType: get<string>("eventType") ?? fallback.eventType,
    category: get<string>("category") ?? fallback.category,
    humanLabel: fallback.humanLabel,
    rawTarget: fallback.rawTarget,
    actor,
    target,
    locationId: get<string>("locationId") ?? fallback.locationId,
    locationName: fallback.locationName,
    requestId: get<string>("requestId") ?? fallback.requestId,
    sourceIp: get<string>("sourceIp") ?? fallback.sourceIp,
    changes: fallback.changes,
    detailsText: fallback.detailsText,
  };

  return {
    evt,
    rawDetails: get<Record<string, unknown>>("details"),
    changesJson: get<unknown>("changes"),
  };
}
